import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import DataMatrixInput from './form/DataMatrixInput.tsx';
import aiDescription from '../utilities/dataMatrixCodec.js';
import xxhash from 'xxhash-wasm';
import endpoints from '../common/endpoints.ts';
import { toast } from '../func.ts';
import { useOrderHashStore } from '../orderHashStore.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../common/api/index.ts';
import { MedAPI } from '../common/api/med.ts';
import useActingAsId from '../hooks/useActingAsId.ts';
import { OrderHashData } from '../common/api/orderHashMicroservice.ts';
import QuarantineMedModal from '../pages/quarantineMeds/QuarantineMedModal.tsx';
import useCurrentCustomerLocationId from '../hooks/useCurrentCustomerLocation.ts';

export const orderHashKey =
  endpoints.orderHashMicroservice.read.hashTable.by.userId();
const SALT = 'salt';
const PEPPER = 'pepper';
const PrimeMod = 2999;

export const getLongestMatch = (possibleMatches: string[]) => {
  return possibleMatches.reduce(
    (prev: string, curr) => (prev.length > curr.length ? prev : curr),
    ''
  );
};

export const getPrefix = (prefixes: string[], superString: string) => {
  const possibleMatches = prefixes.filter((prefix: string) =>
    superString.startsWith(prefix)
  );

  return getLongestMatch(possibleMatches);
};

const UniversalScanner = (): React.JSX.Element => {
  const { actingAsId } = useActingAsId();
  const previousActingAsId = useRef(actingAsId);
  const { customerLocationId } = useCurrentCustomerLocationId();

  const hasherRef = useRef<any>(null);
  const hashDataFromStore = useOrderHashStore(state => state.hashData);
  const hasHydrated = useOrderHashStore(state => state.hasHydrated);
  const mergeHashData = useOrderHashStore(state => state.mergeHashData);

  const modal = useRef<HTMLDivElement>(null);
  const [showInput, setShowInput] = useState<boolean>(false);
  const [milliseconds, setMilliseconds] = useState<number | null>(null);

  const [quarantineModalIsOpen, setQuarantineModalIsOpen] = useState(false);
  const [quarantineMedData, setQuarantineMedData] = useState({
    lotNumber: '',
    expirationDate: '',
    gtin: '',
    serialNumber: '',
    customerLocationId: 0,
  });
  const [scanData, setScanData] = useState({
    lotNumber: '',
    expirationDate: '',
    gtin: '',
    serialNumber: '',
  });
  const [note, setNote] = useState('');

  const noteOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    setNote(value);
  };

  const { data: hashData } = useQuery({
    enabled: hasHydrated && milliseconds !== null,
    queryKey: [orderHashKey, milliseconds, actingAsId],
    queryFn: (): Promise<OrderHashData | null> => {
      let ms = milliseconds ?? 0;
      if (actingAsId !== previousActingAsId.current) {
        ms = 0;
        previousActingAsId.current = actingAsId;
      }
      return api.orderHashMicroservice.read.hashTable.by.userId(ms);
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!hashData) return;
    mergeHashData(hashData);
  }, [hashData]);

  useEffect(() => {
    if (!hasHydrated) return;

    const nextMilliseconds = hashDataFromStore?.millisecondsSinceEpoch ?? 0;

    setMilliseconds(nextMilliseconds);
  }, [hasHydrated]);

  useLayoutEffect(() => {
    const { current } = modal;
    if (current)
      M.Modal.init(current, {
        onCloseEnd: () => setShowInput(false),
      });

    return () => {
      if (current) M.Modal.getInstance(current)?.destroy();
    };
  }, []);

  const getModal = () =>
    modal.current ? M.Modal.getInstance(modal.current) : null;

  useLayoutEffect(() => {
    const modal = getModal();
    if (modal) showInput ? modal.open() : modal.close();
  }, [showInput]);

  const getDocumentIds = useCallback(
    async (data: { [key: string]: string }) => {
      if (!hashDataFromStore) {
        throw new Error('hashData not found');
      }
      const gtin = data[aiDescription.GLOBAL_TRADE_ITEM_NUMBER]
        ? data[aiDescription.GLOBAL_TRADE_ITEM_NUMBER]
        : '';
      const serialShippingContainerCode = data[
        aiDescription.SERIAL_SHIPPING_CONTAINER_CODE
      ]
        ? data[aiDescription.SERIAL_SHIPPING_CONTAINER_CODE]
        : '';

      const serial = data[aiDescription.SERIAL_NUMBER];

      let identifier = '';
      if (gtin) {
        const ndc = gtin.slice(2, -1);
        identifier = `${ndc}.${serial}`;
      } else if (serialShippingContainerCode) {
        const prefix = getPrefix(
          hashDataFromStore.containerPrefixes,
          serialShippingContainerCode.slice(1)
        );
        if (!prefix) throw new Error('matching prefix not found for container');
        identifier = `${prefix}.${serialShippingContainerCode[0]}${serialShippingContainerCode.slice(prefix.length + 1, -1)}`;
      }

      const medData = {
        lotNumber: data['BATCH/LOT NUMBER'] || '',
        expirationDate: data['EXPIRATION DATE (YYMMDD)'] || '',
        gtin: data['GLOBAL TRADE ITEM NUMBER (GTIN)'] || '',
        serialNumber: data['SERIAL NUMBER'] || '',
      };
      setScanData(medData);

      if (!hasherRef.current) {
        hasherRef.current = await xxhash();
      }
      const hasher = hasherRef.current;
      const sgtinKey = hasher.h32(identifier) % PrimeMod;
      const sgtinSaltKey = hasher.h32(`${identifier}${SALT}`) % PrimeMod;
      const sgtinPepperKey = hasher.h32(`${identifier}${PEPPER}`) % PrimeMod;

      const documentIds = hashDataFromStore.sgtinHashTable[`${sgtinKey}`] ?? [];
      const saltDocumentIds =
        hashDataFromStore.sgtinHashTableSalted[`${sgtinSaltKey}`] ?? [];
      const pepperDocumentIds =
        hashDataFromStore.sgtinHashTablePeppered[`${sgtinPepperKey}`] ?? [];

      const arrays = [documentIds, saltDocumentIds, pepperDocumentIds];
      const intersection = arrays.reduce((acc, current) => {
        return acc.filter(value => current.includes(value));
      });

      return { intersection, identifier };
    },
    [hashDataFromStore]
  );

  const { mutateAsync: receiveMed } = useMutation({
    mutationKey: [endpoints.med.update.receive.many.by.serialNumber()],
    mutationFn: ({
      documentIds,
      serialNumber,
    }: MedAPI.Request.UpdateReceiveManyBySerialNumber) =>
      api.med.update.receive.many.by.serialNumber({
        documentIds,
        serialNumber,
      }),

    onSuccess: data => {
      if (data?.payload?.msg.includes('Med Not Found')) {
        if (!scanData) {
          return;
        }

        setQuarantineModalIsOpen(true);

        return setQuarantineMedData({
          ...scanData,
          customerLocationId: customerLocationId || 0,
        });
      }
    },
  });

  const onScan = async (data: { [key: string]: string }) => {
    try {
      const { intersection: documentIds, identifier } =
        await getDocumentIds(data);
      const serialNumber = identifier.split('.')[1];
      await receiveMed({ documentIds, serialNumber });
    } catch (err: any) {
      if (!scanData) {
        return toast({
          payload: {
            msg: 'Med not Found. Try receiving med from the My Orders page.',
            class: 'red white-text',
          },
        });
      } else if (err.toString().includes('matching prefix')) {
        return toast({
          payload: {
            msg: 'Matching prefix not found for container. Try receiving med from the My Orders page.',
            class: 'red white-text',
          },
        });
      }
    }
  };

  const searchMed = () => {
    setShowInput(true);
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9999,
      }}
    >
      <div
        ref={modal}
        className="modal"
        style={{
          width: '90%',
          maxWidth: '800px',
          height: 'auto',
          maxHeight: '85%',
        }}
      >
        <div className="modal-content">
          <h5 style={{ textAlign: 'center' }}>Scan Med</h5>
          {showInput && (
            <DataMatrixInput
              id={'universal-scanner'}
              isLoading={false}
              onScan={onScan}
              disabled={false}
              hidden={!showInput}
              allowPassiveInput={true}
              placeholder={'bar code'}
            />
          )}
        </div>
      </div>
      {hashDataFromStore && (
        <button
          className="btn-floating btn-large blue"
          onClick={searchMed}
          type="button"
          disabled={false}
        >
          <i className="material-icons">qr_code_scanner</i>
        </button>
      )}
      <QuarantineMedModal
        isOpen={quarantineModalIsOpen}
        setIsOpen={setQuarantineModalIsOpen}
        quarantineMedData={quarantineMedData}
        note={note}
        setNote={setNote}
        onChange={noteOnChange}
      />
    </div>
  );
};

export default UniversalScanner;
